import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { formatKr, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Scale, Loader2, ArrowRight, Link2 } from "lucide-react";

/**
 * Dashbord-kort «Klar for sanering»: krediterte fakturaer som har en kreditnota med samme beløp og kunde,
 * og som kan saneres med ett klikk (per rad eller alle). Skjules når det ikke finnes forslag.
 */
export const SettleSuggestionsCard = ({ initial, onChanged, className }) => {
  const [data, setData] = useState(initial || null);
  const [busy, setBusy] = useState(""); // invoice_id | "all"
  const [confirm, setConfirm] = useState(null); // { item } | { all: true }

  const load = useCallback(() => api.get("/sales/settle-suggestions").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { if (!initial) load(); }, [initial, load]);

  const items = data?.items || [];
  const count = data?.count ?? items.length;
  if (!count) return null;

  const settleOne = async (item) => {
    await api.post(`/sales/invoices/${item.invoice.id}/settle`, { counterpart_id: item.credit_note.id });
  };

  const run = async () => {
    const target = confirm;
    setConfirm(null);
    if (!target) return;
    if (target.all) {
      setBusy("all");
      let ok = 0;
      const failed = [];
      for (const it of items) {
        try { await settleOne(it); ok += 1; } catch (e) { failed.push(`${it.invoice.number_display}: ${errorMessage(e)}`); }
      }
      if (failed.length) toast.warning(`${ok} sanert, ${failed.length} feilet`, { description: failed.slice(0, 3).join(" · ") });
      else toast.success(`${ok} par sanert`, { description: "Dokumentene ligger nå under fanen «Sanert»." });
    } else {
      setBusy(target.item.invoice.id);
      try {
        await settleOne(target.item);
        toast.success(`Faktura ${target.item.invoice.number_display} sanert mot kreditnota ${target.item.credit_note.number_display}`);
      } catch (e) {
        toast.error(errorMessage(e));
      }
    }
    setBusy("");
    await load();
    onChanged?.();
  };

  return (
    <>
      <Card className={cn("border-[#D7DCFF] dark:border-[#818CF8]/40", className)} data-testid="settle-suggestions-card">
        <CardHeader className="pb-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <CardTitle className="text-base flex items-center gap-2"><Scale className="h-4 w-4 text-primary" /> Klar for sanering</CardTitle>
              <CardDescription>{count} {count === 1 ? "kreditert faktura har" : "krediterte fakturaer har"} en kreditnota med samme beløp. Saner dem for å fjerne begge fra de daglige oversiktene.{data?.amount ? ` Totalt ${formatKr(data.amount)}.` : ""}</CardDescription>
            </div>
            {count > 1 ? (
              <Button size="sm" variant="outline" onClick={() => setConfirm({ all: true })} disabled={!!busy} className="shrink-0" data-testid="settle-suggestions-all-button">
                {busy === "all" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Scale className="h-4 w-4 mr-1.5" />} Saner alle ({count})
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y" data-testid="settle-suggestions-list">
            {items.map((it) => (
              <li key={it.invoice.id} className="flex flex-col gap-2 px-4 sm:px-6 py-3 sm:flex-row sm:items-center" data-testid={`settle-suggestion-${it.invoice.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{it.invoice.customer_name || "Ukjent kunde"}</p>
                  <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1.5">
                    <Link to={`/faktura/fakturaer/${it.invoice.id}`} className="hover:underline" data-testid={`settle-suggestion-invoice-link-${it.invoice.id}`}>Faktura {it.invoice.number_display}</Link>
                    <ArrowRight className="h-3 w-3" />
                    <Link to={`/faktura/fakturaer/${it.credit_note.id}`} className="hover:underline" data-testid={`settle-suggestion-credit-link-${it.invoice.id}`}>Kreditnota {it.credit_note.number_display}</Link>
                    <span>· {formatDate(it.credit_note.issue_date)}</span>
                    {it.linked ? <span className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0 text-[11px] status-avtalegiro"><Link2 className="h-3 w-3" /> Koblet</span> : null}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-3 sm:justify-end">
                  <p className="text-sm font-medium tnum whitespace-nowrap" data-testid={`settle-suggestion-amount-${it.invoice.id}`}>{formatKr(it.amount)}</p>
                  <Button size="sm" onClick={() => setConfirm({ item: it })} disabled={!!busy} data-testid={`settle-suggestion-button-${it.invoice.id}`}>
                    {busy === it.invoice.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Scale className="h-4 w-4 mr-1.5" />} Saner
                  </Button>
                </div>
              </li>
            ))}
            {count > items.length ? <li className="px-6 py-2.5 text-xs text-muted-foreground">+ {count - items.length} til. <Link to="/faktura/fakturaer?status=kreditert" className="underline">Se krediterte fakturaer</Link></li> : null}
          </ul>
        </CardContent>
      </Card>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-card" data-testid="settle-suggestions-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.all ? `Sanere alle ${count} par?` : `Sanere faktura ${confirm?.item?.invoice.number_display}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.all
                ? `Alle ${count} krediterte fakturaer utlignes mot sine kreditnotaer (totalt ${formatKr(data?.amount)}). Begge dokumentene i hvert par får status «Sanert» og forsvinner fra daglige oversikter. Du kan oppheve hver sanering senere.`
                : `Faktura ${confirm?.item?.invoice.number_display} og kreditnota ${confirm?.item?.credit_note.number_display} (${formatKr(confirm?.item?.amount)}) får status «Sanert» og forsvinner fra daglige oversikter. Du kan oppheve saneringen senere fra dokumentet.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settle-suggestions-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={run} data-testid="settle-suggestions-confirm"><Scale className="h-4 w-4 mr-2" /> {confirm?.all ? "Saner alle" : "Saner"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
